import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Avatar,
} from 'antd';
import {
  ArrowRightOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import MainLayout from '../../layouts/MainLayout';
import StandardFilterPanel from '../../components/hr/StandardFilterPanel';
import { useAuth } from '../../contexts/AuthContext';
import { resolveAvatarSrc } from '../../utils/avatarUtils';
import { readCachedAuthUserId } from '../../utils/cachedAuthUser';
import { followUpsApi, leadSettingsApi, employeesApi } from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';
import {
  buildCommonDatePresets,
  parseJalaliDateRangeValue,
  toFilterSelectId,
} from '../../utils/filterDateUtils';
import { resolveFaIconClasses } from '../../utils/faIconUtils';
import {
  followUpStatusLabel,
  formatFollowUpDateWithRelative,
  resolveFollowUpEntity,
  resolveFollowUpActivityHref,
  resolveFollowUpRelatedType,
  FOLLOW_UPS_LIST_PATH,
  FOLLOW_UPS_LIST_RETURN_STORAGE_KEY,
} from '../../utils/followUpDisplayUtils';
import { resolveFollowUpCompletedAt } from '../../utils/followUpUtils';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { useListReturnState, LIST_RESTORE_STATE_KEY } from '../../hooks/useListReturnState';

const buildDefaultFilters = (userId = null) => {
  const end = moment();
  const start = moment().subtract(30, 'days');
  return {
    startDate: start.format('jYYYY/jMM/jDD'),
    endDate: end.format('jYYYY/jMM/jDD'),
    status: 'pending',
    follow_up_activity_id: 'all',
    assign_to: toFilterSelectId(userId ?? readCachedAuthUserId()),
    search: '',
  };
};

const parseFiltersFromQuery = (searchParams, userId = null) => {
  const defaults = buildDefaultFilters(userId);
  const get = (key, fallback) => searchParams.get(key) ?? fallback;
  return {
    startDate: get('startDate', defaults.startDate),
    endDate: get('endDate', defaults.endDate),
    status: get('status', defaults.status),
    follow_up_activity_id: toFilterSelectId(
      searchParams.get('follow_up_activity_id'),
      defaults.follow_up_activity_id,
    ),
    assign_to: toFilterSelectId(searchParams.get('assign_to'), defaults.assign_to),
    search: '',
  };
};

const resolveFollowUpsListPage = (res, body, list, page, pageSize, prevTotal = 0) => {
  const pg = body.pagination || res?.pagination || body.meta || res?.meta || {};
  const explicitTotal =
    pg.total ??
    pg.total_count ??
    pg.count ??
    body.total ??
    res?.total ??
    null;

  if (explicitTotal != null && !Number.isNaN(Number(explicitTotal))) {
    return {
      rows: list,
      total: Number(explicitTotal),
    };
  }

  const lastPage = pg.last_page ?? pg.lastPage;
  if (lastPage != null && !Number.isNaN(Number(lastPage))) {
    const perPage = Number(pg.per_page ?? pg.pageSize ?? pageSize) || pageSize;
    const total =
      page === Number(lastPage)
        ? (Number(lastPage) - 1) * perPage + list.length
        : Number(lastPage) * perPage;
    return { rows: list, total };
  }

  if (list.length > pageSize) {
    const total = list.length;
    const start = (page - 1) * pageSize;
    return {
      rows: list.slice(start, start + pageSize),
      total,
    };
  }

  const listLen = list.length;
  let total;
  if (listLen >= pageSize) {
    total = Math.max(prevTotal, page * pageSize + 1);
  } else {
    total = (page - 1) * pageSize + listLen;
  }

  return { rows: list, total };
};

const buildQueryParams = ({ filters, searchText, pagination }) => {
  const params = new URLSearchParams();
  if (searchText) params.set('q', searchText);
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value == null || value === '' || value === 'all' || key === 'search') return;
    params.set(key, String(value));
  });
  if (pagination?.current && pagination.current !== 1) params.set('page', String(pagination.current));
  if (pagination?.pageSize && pagination.pageSize !== 10) {
    params.set('per_page', String(pagination.pageSize));
  }
  return params;
};

const ActivityCell = ({ activity, row, onNavigate }) => {
  if (!activity) return '—';
  const resolved = resolveFaIconClasses(activity.icon);
  const href = resolveFollowUpActivityHref(row);
  const content = (
    <span className="inline-flex items-center gap-2">
      {resolved ? (
        <i className={resolved.className} style={{ color: activity.color || '#7c3aed' }} aria-hidden />
      ) : null}
      <span>{activity.name}</span>
    </span>
  );

  if (!href) return content;

  return (
    <Button
      type="link"
      size="small"
      className="h-auto p-0 custom-font text-right"
      onClick={() => onNavigate(href)}
    >
      {content}
    </Button>
  );
};

const FollowUpDateCell = ({ value, row }) => {
  const completedAt = resolveFollowUpCompletedAt(row);
  if (completedAt) {
    return (
      <div className="leading-snug custom-font">
        <div className="text-green-700 dark:text-green-400 font-medium">انجام: {completedAt}</div>
        {value ? (
          <div className="text-xs text-gray-400 line-through mt-0.5">
            {formatFollowUpDateWithRelative(value).main}
          </div>
        ) : null}
      </div>
    );
  }
  const { main, relative } = formatFollowUpDateWithRelative(value);
  return (
    <div className="leading-snug">
      <div>{main || '—'}</div>
      {relative ? (
        <div className="text-xs text-gray-500 dark:text-gray-400">{relative}</div>
      ) : null}
    </div>
  );
};

const AllFollowUps = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const assignDefaultAppliedRef = useRef(false);
  const skipUrlSyncRef = useRef(false);
  const dashboardReturnToRef = useRef('/dashboard/private');

  const [filters, setFilters] = useState(() =>
    parseFiltersFromQuery(searchParams, readCachedAuthUserId()),
  );
  const [searchText, setSearchText] = useState(() => searchParams.get('q') || '');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [activities, setActivities] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState({
    current: Number(searchParams.get('page')) || 1,
    pageSize: Number(searchParams.get('per_page')) || 10,
    total: 0,
  });

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      searchText,
      pagination: { current: pagination.current, pageSize: pagination.pageSize },
    }),
    [filters, searchText, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(
    captureCurrentListState,
    (restored) => {
      skipUrlSyncRef.current = true;
      if (restored.filters) {
        setFilters((prev) => ({
          ...prev,
          ...restored.filters,
          follow_up_activity_id: toFilterSelectId(
            restored.filters.follow_up_activity_id,
            prev.follow_up_activity_id || 'all',
          ),
          assign_to: toFilterSelectId(restored.filters.assign_to, prev.assign_to),
        }));
      }
      if (restored.searchText != null) setSearchText(restored.searchText);
      if (restored.pagination) {
        setPagination((prev) => ({
          ...prev,
          current: restored.pagination.current || 1,
          pageSize: restored.pagination.pageSize || prev.pageSize,
        }));
      }
    },
  );

  const openFollowUpDetail = (row) => {
    const entity = resolveFollowUpEntity(row);
    const href = resolveFollowUpActivityHref(row) || entity.href;
    if (!href) return;
    navigateToDetail(href, {}, {
      listPath: `${location.pathname}${location.search}`,
      listLabel: 'همه فعالیت‌ها',
      entityLabel: entity.name !== '—' ? entity.name : (row.activity?.name || 'فعالیت'),
    });
  };

  useEffect(() => {
    const incomingReturn = location.state?.returnTo;
    const isListRestore = Boolean(
      location.state?.[LIST_RESTORE_STATE_KEY] || location.state?.listState,
    );
    if (incomingReturn && !isListRestore && !String(incomingReturn).startsWith(FOLLOW_UPS_LIST_PATH)) {
      dashboardReturnToRef.current = incomingReturn;
      try {
        sessionStorage.setItem(FOLLOW_UPS_LIST_RETURN_STORAGE_KEY, incomingReturn);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      const stored = sessionStorage.getItem(FOLLOW_UPS_LIST_RETURN_STORAGE_KEY);
      if (stored) dashboardReturnToRef.current = stored;
    } catch {
      /* ignore */
    }
  }, [location.state]);

  useEffect(() => {
    if (!user?.id || assignDefaultAppliedRef.current) return;
    // فقط اگر URL مقدار assign_to نگذاشته باشد، کاربر جاری را پیش‌فرض کن
    if (!searchParams.has('assign_to')) {
      setFilters((prev) => (prev.assign_to === 'all' ? { ...prev, assign_to: user.id } : prev));
    }
    assignDefaultAppliedRef.current = true;
  }, [user?.id, searchParams]);

  // همگام‌سازی فیلترها با URL تا بازگشت مرورگر هم فیلترها را نگه دارد
  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
    }
    const next = buildQueryParams({ filters, searchText, pagination });
    const nextStr = next.toString();
    const curStr = searchParams.toString();
    if (nextStr !== curStr) {
      setSearchParams(next, { replace: true, state: location.state || {} });
    }
  }, [filters, searchText, pagination.current, pagination.pageSize]);

  const loadOptions = useCallback(async () => {
    try {
      const [actRes, empRes] = await Promise.all([
        leadSettingsApi.getSettings('follow-up'),
        employeesApi.getAll({ status: 'active' }),
      ]);
      const actData = actRes?.data || actRes || {};
      setActivities(actData.follow_up_activities || []);
      const empList = empRes?.data?.data || empRes?.data || empRes || [];
      setEmployees(Array.isArray(empList) ? empList : []);
    } catch (e) {
      console.warn('filter options load failed', e);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const query = {
        ...filters,
        search: searchText.trim() || undefined,
        page: pagination.current,
        per_page: pagination.pageSize,
      };
      const res = await followUpsApi.getAll(query);
      const body = res?.data || res || {};
      const rawList = body.followups || res?.followups || [];
      const list = Array.isArray(rawList) ? rawList : [];
      const { rows, total } = resolveFollowUpsListPage(
        res,
        body,
        list,
        pagination.current,
        pagination.pageSize,
        pagination.total,
      );
      setRows(rows);
      setPagination((prev) => ({
        ...prev,
        total: Number(total) || 0,
      }));
    } catch (e) {
      setRows([]);
      handleApiError(e, 'خطا در دریافت فعالیت‌ها');
    } finally {
      setLoading(false);
    }
  }, [filters, searchText, pagination.current, pagination.pageSize]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetToFirstPage = () => setPagination((prev) => ({ ...prev, current: 1 }));

  const handleSearchChange = (value) => {
    setSearchText(value);
    resetToFirstPage();
  };

  const handleFilterChange = (key, value) => {
    if (key === 'dateRange') {
      const { startDate, endDate } = parseJalaliDateRangeValue(value);
      setFilters((prev) => ({ ...prev, startDate, endDate }));
      resetToFirstPage();
      return;
    }
    let nextValue = value == null || value === '' ? 'all' : value;
    if (key === 'follow_up_activity_id' || key === 'assign_to') {
      nextValue = toFilterSelectId(nextValue, 'all');
    }
    setFilters((prev) => ({ ...prev, [key]: nextValue }));
    resetToFirstPage();
  };

  const handleResetFilters = () => {
    setFilters(buildDefaultFilters(user?.id));
    setSearchText('');
    resetToFirstPage();
  };

  const handleTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize,
    }));
  };

  const quickFilters = useMemo(
    () => [
      {
        key: 'dateRange',
        type: 'dateRange',
        label: 'بازه تاریخ',
        placeholder: 'از تاریخ — تا تاریخ',
        value: filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : undefined,
      },
      {
        key: 'datePreset',
        type: 'datePreset',
        label: 'بازه سریع',
        placeholder: 'بازه سریع',
        options: buildCommonDatePresets(),
        onPreset: (range) => {
          setFilters((prev) => ({ ...prev, ...range }));
          resetToFirstPage();
        },
      },
      {
        key: 'status',
        type: 'select',
        label: 'وضعیت',
        placeholder: 'همه',
        value: filters.status === 'all' ? undefined : filters.status,
        options: [
          { value: 'pending', label: 'در انتظار' },
          { value: 'completed', label: 'انجام‌شده' },
        ],
        allowClear: true,
      },
      {
        key: 'follow_up_activity_id',
        type: 'select',
        label: 'نوع فعالیت',
        placeholder: 'همه فعالیت‌ها',
        value: filters.follow_up_activity_id === 'all' ? undefined : Number(filters.follow_up_activity_id),
        options: activities.map((a) => ({ value: Number(a.id), label: a.name })),
        showSearch: true,
        allowClear: true,
      },
      {
        key: 'assign_to',
        type: 'select',
        label: 'مسئول',
        placeholder: 'همه',
        value: filters.assign_to === 'all' ? undefined : Number(filters.assign_to),
        options: employees.map((e) => ({ value: Number(e.id), label: e.name })),
        showSearch: true,
        allowClear: true,
      },
    ],
    [filters, activities, employees],
  );

  const columns = [
    {
      title: 'عنوان فعالیت',
      key: 'activity',
      width: 180,
      render: (_, row) => (
        <ActivityCell activity={row.activity} row={row} onNavigate={() => openFollowUpDetail(row)} />
      ),
    },
    {
      title: 'مربوط به',
      key: 'related_type',
      width: 100,
      render: (_, row) => {
        const related = resolveFollowUpRelatedType(row);
        if (!related.type) return '—';
        return (
          <Tag color={related.type === 'deal' ? 'purple' : 'blue'} className="m-0">
            {related.label}
          </Tag>
        );
      },
    },
    {
      title: 'معامله/سرنخ مرتبط',
      key: 'entity',
      ellipsis: true,
      render: (_, row) => {
        const entity = resolveFollowUpEntity(row);
        if (!entity.href) return entity.name;
        return (
          <Button
            type="link"
            size="small"
            className="h-auto p-0 custom-font"
            onClick={() => openFollowUpDetail(row)}
          >
            {entity.name}
          </Button>
        );
      },
    },
    {
      title: 'مسئول انجام',
      key: 'assign_to',
      width: 160,
      render: (_, row) => {
        const assignee = row.assign_to;
        if (!assignee?.name) return '—';
        return (
          <span className="inline-flex items-center gap-2">
            <Avatar
              size={24}
              src={resolveAvatarSrc(assignee.image_url)}
              icon={<UserOutlined />}
            />
            <span className="truncate">{assignee.name}</span>
          </span>
        );
      },
    },
    {
      title: 'تاریخ فعالیت',
      key: 'date',
      width: 160,
      render: (_, row) => <FollowUpDateCell value={row.next_follow_up_date} row={row} />,
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s) => (
        <Tag color={s === 'completed' ? 'green' : 'gold'}>{followUpStatusLabel(s)}</Tag>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="custom-font">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-900/25">
                <UnorderedListOutlined className="text-xl text-violet-600 dark:text-violet-300" />
              </div>
              <div>
                <h1 className="m-0 text-2xl font-bold text-gray-800 dark:text-gray-100">همه فعالیت‌ها</h1>
                <p className="m-0 text-sm text-gray-500">پیگیری‌های سرنخ و معامله</p>
              </div>
            </div>
            <Space wrap>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(dashboardReturnToRef.current || '/dashboard/private')}
              >
                بازگشت به داشبورد
              </Button>
              <Button icon={<ReloadOutlined />} loading={loading} onClick={loadData}>
                بروزرسانی
              </Button>
            </Space>
          </div>

          <div className="space-y-4 bg-gray-50 p-6 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="border border-gray-100 dark:border-gray-700">
              <StandardFilterPanel
                searchValue={searchText}
                onSearchChange={handleSearchChange}
                searchPlaceholder="جستجو..."
                quickFilters={quickFilters}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                compactFilterLayout
                totalCount={pagination.total}
                showTotalCount
              />
            </Card>

            {loading ? (
              <TableSkeleton />
            ) : (
              <Card className="border border-gray-100 dark:border-gray-700">
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={rows}
                  onChange={handleTableChange}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showTotal: (total) => `مجموع ${total} مورد`,
                    pageSizeOptions: ['10', '15', '25', '50'],
                  }}
                  scroll={{ x: 1020 }}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AllFollowUps;
